// FILE: frontend/src/components/PostCard.js  (NEW FILE)
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, MessageCircle, Share2, MoreHorizontal, Film, Trash2, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PostCard({ post, currentUser, onDelete }) {
  const [liked, setLiked]           = useState(post.is_liked || false);
  const [likeCount, setLikeCount]   = useState(post.like_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments]     = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showMenu, setShowMenu]     = useState(false);
  const [shareCount, setShareCount] = useState(post.share_count || 0);

  const isOwn = currentUser?.user_id === post.user_id;
  const commentCount = post.comment_count || 0;

  const handleLike = async () => {
    // Optimistic update
    setLiked(prev => !prev);
    setLikeCount(prev => liked ? prev - 1 : prev + 1);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/posts/${post.post_id}/like`,
        {},
        { withCredentials: true }
      );
      setLiked(res.data.liked);
      setLikeCount(res.data.like_count);
    } catch (err) {
      // Roll back optimistic update
      setLiked(prev => !prev);
      setLikeCount(prev => liked ? prev + 1 : prev - 1);
      if (err.response?.status === 401) toast.error('Login to like posts');
    }
  };

  const handleToggleComments = async () => {
    setShowComments(prev => !prev);
    if (!commentsLoaded) {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/posts/${post.post_id}/comments`);
        setComments(res.data || []);
        setCommentsLoaded(true);
      } catch { /* ignore */ }
    }
  };

  const handlePostComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;
    if (!currentUser) { toast.error('Login to comment'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/posts/${post.post_id}/comments`,
        { content: newComment },
        { withCredentials: true }
      );
      setComments(prev => [...prev, res.data]);
      setNewComment('');
    } catch (err) {
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/posts/${post.post_id}`, { withCredentials: true });
      toast.success('Post deleted');
      onDelete?.(post.post_id);
    } catch { toast.error('Failed to delete'); }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/post/${post.post_id}`;
    try {
      await navigator.clipboard.writeText(url);
      const res = await axios.post(
        `${BACKEND_URL}/api/posts/${post.post_id}/share`,
        {},
        { withCredentials: true }
      );
      setShareCount(res.data?.share_count ?? (shareCount + 1));
      toast.success('Link copied!');
    } catch {
      toast.error('Failed to share');
    }
  };

  const timeAgo = formatDistanceToNow(new Date(post.created_at), { addSuffix: true });

  return (
    <article className="bg-card border border-white/6 rounded-2xl overflow-hidden hover:border-white/12 transition-colors duration-200">

      {/* ── HEADER ───────────────────────────────────────── */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <Link to={`/profile/${post.user_id}`} className="flex items-center gap-3 group">
          <img
            src={post.user_picture}
            alt={post.user_name}
            className="w-10 h-10 rounded-full border border-white/10 group-hover:border-primary/50 transition-colors"
          />
          <div>
            <p className="font-semibold text-sm text-white group-hover:text-primary transition-colors leading-tight">
              {post.user_name}
            </p>
            <p className="text-xs text-muted-foreground">{timeAgo}</p>
          </div>
        </Link>

        <div className="relative">
          <button
            onClick={() => setShowMenu(prev => !prev)}
            className="p-1.5 text-muted-foreground hover:text-white transition-colors rounded-lg hover:bg-white/5"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-8 bg-card border border-white/10 rounded-xl shadow-2xl z-20 w-40 py-1 overflow-hidden">
              <button
                onClick={handleShare}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
              >
                <Share2 className="w-3.5 h-3.5" /> Copy link
              </button>
              {isOwn && (
                <button
                  onClick={handleDelete}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete post
                </button>
              )}
              <button
                onClick={() => setShowMenu(false)}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-500 hover:bg-white/5 transition-colors"
              >
                <X className="w-3.5 h-3.5" /> Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── CONTENT ──────────────────────────────────────── */}
      <div className="px-5 pb-3">
        <p className="text-gray-100 leading-relaxed text-[15px] whitespace-pre-wrap">
          {post.content}
        </p>
      </div>

      {/* ── IMAGE ────────────────────────────────────────── */}
      {post.image_url && (
        <div className="mx-5 mb-3 rounded-xl overflow-hidden border border-white/8">
          <img
            src={post.image_url}
            alt="Post"
            className="w-full max-h-[480px] object-cover"
            loading="lazy"
          />
        </div>
      )}

      {/* ── MOVIE TAG ────────────────────────────────────── */}
      {post.tagged_movie_id && (
        <Link
          to={`/${post.tagged_media_type || 'movie'}/${post.tagged_movie_id}`}
          className="mx-5 mb-3 flex items-center gap-3 bg-white/4 border border-white/8 rounded-xl p-3 hover:border-primary/40 hover:bg-primary/5 transition-all group"
        >
          {post.tagged_movie_poster ? (
            <img
              src={`https://image.tmdb.org/t/p/w92${post.tagged_movie_poster}`}
              alt={post.tagged_movie_title}
              className="w-10 h-[60px] rounded-lg object-cover border border-white/10 flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-[60px] rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
              <Film className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-primary mb-0.5">
              {post.tagged_media_type === 'tv' ? 'TV Show' : 'Movie'}
            </p>
            <p className="font-semibold text-sm text-white group-hover:text-primary transition-colors line-clamp-1">
              {post.tagged_movie_title}
            </p>
          </div>
          <Film className="w-4 h-4 text-muted-foreground ml-auto flex-shrink-0" />
        </Link>
      )}

      {/* ── ACTIONS ──────────────────────────────────────── */}
      <div className="flex items-center gap-1 px-4 pb-4 pt-1 border-t border-white/5 mt-1">
        <button
          onClick={handleLike}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all active:scale-95 ${
            liked
              ? 'text-red-400 bg-red-500/10'
              : 'text-muted-foreground hover:text-red-400 hover:bg-red-500/8'
          }`}
        >
          <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
          <span>{likeCount > 0 ? likeCount : ''}</span>
        </button>

        <button
          onClick={handleToggleComments}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-blue-400 hover:bg-blue-500/8 transition-all"
        >
          <MessageCircle className="w-4 h-4" />
          <span>{commentCount > 0 ? commentCount : ''}</span>
        </button>

        <button
          onClick={handleShare}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-green-400 hover:bg-green-500/8 transition-all ml-auto"
        >
          <Share2 className="w-4 h-4" />
          <span>{shareCount > 0 ? shareCount : ''}</span>
        </button>
      </div>

      {/* ── COMMENTS ─────────────────────────────────────── */}
      {showComments && (
        <div className="border-t border-white/5 bg-black/20 px-5 py-4 space-y-4">
          {comments.length === 0 && commentsLoaded && (
            <p className="text-sm text-muted-foreground text-center py-2">
              No comments yet. Be the first!
            </p>
          )}

          {comments.map(c => (
            <div key={c.comment_id} className="flex gap-3">
              <img
                src={c.user_picture}
                alt={c.user_name}
                className="w-7 h-7 rounded-full border border-white/10 flex-shrink-0 mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <div className="bg-white/4 rounded-xl px-3 py-2.5">
                  <Link
                    to={`/profile/${c.user_id}`}
                    className="text-xs font-bold text-white hover:text-primary transition-colors"
                  >
                    {c.user_name}
                  </Link>
                  <p className="text-sm text-gray-200 mt-0.5 leading-snug">{c.content}</p>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 px-1">
                  {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                </p>
              </div>
            </div>
          ))}

          {currentUser && (
            <form onSubmit={handlePostComment} className="flex gap-3 pt-1">
              <img
                src={currentUser.picture}
                alt={currentUser.name}
                className="w-7 h-7 rounded-full border border-white/10 flex-shrink-0 mt-1"
              />
              <div className="flex-1 flex gap-2">
                <input
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder="Write a comment…"
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-primary/50 focus:bg-white/8 transition-all"
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || submitting}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-bold disabled:opacity-40 hover:bg-primary/90 transition-all active:scale-95"
                >
                  Post
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </article>
  );
}
